import React, {memo, useMemo} from 'react';
import {useTheme, styled} from '@packages/utilities/styled';

import {useTutorial} from './useTutorial';
import {Status} from './constants';
import {FinePrint} from '@packages/systems/design-system/Text';
// eslint-disable-next-line webflow/no-dangerous-imports
import {useDomNodeBox} from '@packages/systems/ui/shared-ui/DomNodeAnchor';
import {CircleCheckSmall as CheckIcon} from '@packages/systems/ui/icons';

import type {DragDestinationSelector} from './types';

const DRAG_BOX_HEIGHT = 20;
const DRAG_BOX_WIDTH = 90;
const DRAG_BOX_WIDTH_LARGE = 170;
const DRAG_BOX_RADIUS = 3;
const DRAG_BOX_OFFSET = 4;
const DRAG_BOX_BORDER_OFFSET = -2;
const DRAG_TEXT_FONT_SIZE = 22;
const getBorder = (borderColor: string) => `2px dashed ${borderColor}`;

export const InternalTutorialDOMNodeDragIndicator = memo<{
  scrollTop: number;
  scrollLeft: number;
  scale: number;
}>(({scrollTop, scrollLeft, scale}) => {
  const [, , , {anchorPosition, getAnchorNode}] = useTutorial();
  const dragDestinationSelector = useGetDragDestinationSelector();
  const {style, node} = useDomNodeBox(dragDestinationSelector, {
    scrollLeft,
    scrollTop,
    scale,
  });

  const theme = useTheme();
  const {position, __EXPERIMENTAL__highlight} = dragDestinationSelector || {};

  const {style: overrideStyle} = useDomNodeBox(
    (typeof __EXPERIMENTAL__highlight === 'object' &&
      __EXPERIMENTAL__highlight) ||
      null,
    {
      scrollLeft,
      scrollTop,
      scale,
    }
  );

  const isOver = useMemo(() => {
    if (!position || !anchorPosition || !node) {
      return false;
    }

    return anchorPosition === position && getAnchorNode() === node;
  }, [anchorPosition, getAnchorNode, position, node]);

  if (!position || !style || !anchorPosition) {
    return null;
  }

  const {containerStyle, dragIndicatorStyle} = styleDragIndicator(theme, {
    position,
    __EXPERIMENTAL__highlight,
    isOver,
  });

  return (
    <div
      style={{
        ...(overrideStyle || style),
        visibility: 'unset',
        display: 'flex',
        justifyContent: 'center',
        zIndex: theme.zIndexes.zIndexModal,
      }}
    >
      <div style={containerStyle}>
        <FinePrint style={dragIndicatorStyle}>
          DRAG HERE{' '}
          {isOver && (
            <StyledCheckIcon large={Boolean(__EXPERIMENTAL__highlight)} />
          )}
        </FinePrint>
      </div>
    </div>
  );
});

const StyledCheckIcon = styled(CheckIcon)(
  ({theme: {gutters}}) => ({
    marginLeft: gutters.base,
  }),
  {
    large: {
      width: DRAG_TEXT_FONT_SIZE - 2,
      height: DRAG_TEXT_FONT_SIZE - 2,
      position: 'absolute',
      right: 0,
    },
  }
);

// @ts-expect-error - TS7031 - Binding element 'colors' implicitly has an 'any' type. | TS7031 - Binding element 'gutters' implicitly has an 'any' type. | TS7006 - Parameter 'opts' implicitly has an 'any' type.
function styleDragIndicator({colors, gutters}, opts) {
  const bgColor = opts.isOver ? colors.successAlt : colors.accentPrimary;
  const baseContainerStyles = {
    position: 'absolute',
    width: `calc(100% - ${gutters.base * 2}px)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  } as const;

  const baseDragIndicatorStyles = {
    position: 'relative',
    width: DRAG_BOX_WIDTH,
    height: DRAG_BOX_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    background: bgColor,
  } as const;

  if (opts.__EXPERIMENTAL__highlight) {
    return {
      dragIndicatorStyle: {
        ...baseDragIndicatorStyles,
        width: DRAG_BOX_WIDTH_LARGE,
        height: 'auto',
        background: 'transparent',
        color: bgColor,
        fontSize: DRAG_TEXT_FONT_SIZE,
        fontWeight: 600,
      },
      containerStyle: {
        ...baseContainerStyles,
        height: `calc(100% - ${gutters.base}px)`,
        backgroundColor: bgColor + '30', // 30% opacity
        border: getBorder(bgColor),
      },
    };
  }

  switch (opts.position) {
    case 'append':
      return {
        dragIndicatorStyle: {
          ...baseDragIndicatorStyles,
          top: DRAG_BOX_BORDER_OFFSET,
          borderBottomLeftRadius: DRAG_BOX_RADIUS,
          borderBottomRightRadius: DRAG_BOX_RADIUS,
        },
        containerStyle: {
          ...baseContainerStyles,
          borderTop: getBorder(bgColor),
          top: DRAG_BOX_OFFSET,
        },
      };
    case 'before':
      return {
        dragIndicatorStyle: {
          ...baseDragIndicatorStyles,
          borderTopLeftRadius: DRAG_BOX_RADIUS,
          borderTopRightRadius: DRAG_BOX_RADIUS,
          bottom: DRAG_BOX_BORDER_OFFSET,
        },
        containerStyle: {
          ...baseContainerStyles,
          borderBottom: getBorder(bgColor),
          top: -(DRAG_BOX_HEIGHT + DRAG_BOX_OFFSET),
        },
      };
    case 'prepend':
      return {
        dragIndicatorStyle: baseDragIndicatorStyles,
        containerStyle: {
          ...baseContainerStyles,
          borderTop: getBorder(bgColor),
          bottom: -DRAG_BOX_OFFSET,
        },
      };
    case 'after':
      return {
        dragIndicatorStyle: {
          ...baseDragIndicatorStyles,
          borderTopLeftRadius: DRAG_BOX_RADIUS,
          borderTopRightRadius: DRAG_BOX_RADIUS,
          bottom: DRAG_BOX_BORDER_OFFSET,
        },
        containerStyle: {
          ...baseContainerStyles,
          borderBottom: getBorder(bgColor),
          bottom: -DRAG_BOX_OFFSET,
        },
      };
  }

  return {};
}

function useGetDragDestinationSelector(): null | DragDestinationSelector {
  const [currentTutorial, steps] = useTutorial();

  return useMemo(() => {
    if (
      currentTutorial == null ||
      currentTutorial.status !== Status.Active ||
      currentTutorial.currentStepId == null ||
      steps == null
    ) {
      return null;
    }

    const step = steps[currentTutorial.currentStepId];

    return (step && step.ui && step.ui.dragDestinationSelector) || null;
  }, [currentTutorial, steps]);
}
